/**
 * Client-side GeoJSON layer analysis for the import modal.
 * Avoids posting large FeatureCollections to /import/geojson/analyze (DXF still uses the API).
 */

import {
  detectImportSourceCrs,
  resolveImportSourceCrs,
  type ResolvedImportSourceCrs,
} from '@core/shared-bridge';
import { importLayerNameFromFeature, type ImportPreviewFeatureCollection } from './import-geometry-preview.util';

export interface ClientGeoJsonLayerInfo {
  layerName: string;
  featureCount: number;
  geometryTypes: string[];
  sampleProperties: Record<string, unknown>;
}

export interface ClientGeoJsonAnalysis {
  layers: ClientGeoJsonLayerInfo[];
  totalFeatures: number;
  detectedSourceCrs: ResolvedImportSourceCrs;
  sourceCrs: ResolvedImportSourceCrs;
}

export function analyzeGeoJsonClientSide(
  data: ImportPreviewFeatureCollection | null | undefined,
  sourceCrsHint: ResolvedImportSourceCrs | 'auto' = 'auto',
): ClientGeoJsonAnalysis | null {
  if (!data || data.type !== 'FeatureCollection' || !Array.isArray(data.features)) {
    return null;
  }

  const layerMap = new Map<
    string,
    {
      featureCount: number;
      geometryTypes: Set<string>;
      sampleProperties: Record<string, unknown>;
    }
  >();

  for (const feature of data.features) {
    const layerName = importLayerNameFromFeature(feature);
    let info = layerMap.get(layerName);
    if (!info) {
      info = {
        featureCount: 0,
        geometryTypes: new Set(),
        sampleProperties: { ...(feature.properties ?? {}) },
      };
      layerMap.set(layerName, info);
    }
    info.featureCount++;
    const geomType = feature?.geometry?.type;
    if (typeof geomType === 'string' && geomType) {
      info.geometryTypes.add(geomType);
    }
  }

  const detectedSourceCrs = detectImportSourceCrs(data);
  const sourceCrs = resolveImportSourceCrs(data, sourceCrsHint);

  return {
    layers: Array.from(layerMap.entries()).map(([layerName, info]) => ({
      layerName,
      featureCount: info.featureCount,
      geometryTypes: Array.from(info.geometryTypes),
      sampleProperties: info.sampleProperties,
    })),
    totalFeatures: data.features.length,
    detectedSourceCrs,
    sourceCrs,
  };
}
